import { useEffect } from 'react'

/**
 * useContentGuard — lightweight deterrents against casual copying / inspection.
 *
 * IMPORTANT: This is NOT real security. Anyone technical can still view the
 * source (all HTML/CSS/JS reaches the browser). It only discourages casual
 * users from right-clicking to save images, dragging assets, selecting text,
 * or opening DevTools via common shortcuts.
 *
 * Text selection stays enabled inside form fields (input, textarea, and any
 * element marked contenteditable) so the contact form keeps working.
 */
export function useContentGuard() {
  useEffect(() => {
    const isFormField = (el: EventTarget | null): boolean => {
      const node = el as HTMLElement | null
      if (!node || !node.closest) return false
      return !!node.closest('input, textarea, [contenteditable="true"]')
    }

    // Block the right-click context menu
    const onContextMenu = (e: MouseEvent) => {
      e.preventDefault()
    }

    // Block dragging images / links out of the page
    const onDragStart = (e: DragEvent) => {
      e.preventDefault()
    }

    // Block text selection everywhere except form fields
    const onSelectStart = (e: Event) => {
      if (!isFormField(e.target)) {
        e.preventDefault()
      }
    }

    // Block common DevTools / view-source keyboard shortcuts
    const onKeyDown = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase()
      const ctrlOrMeta = e.ctrlKey || e.metaKey

      // F12 — open DevTools
      if (e.key === 'F12') {
        e.preventDefault()
        return
      }

      // Ctrl/Cmd + Shift + I / J / C — DevTools panels
      if (
        ctrlOrMeta &&
        e.shiftKey &&
        (key === 'i' || key === 'j' || key === 'c')
      ) {
        e.preventDefault()
        return
      }

      // Ctrl/Cmd + U — view page source
      if (ctrlOrMeta && key === 'u') {
        e.preventDefault()
        return
      }

      // Ctrl/Cmd + S — save page (allow inside form fields just in case)
      if (ctrlOrMeta && key === 's' && !isFormField(e.target)) {
        e.preventDefault()
        return
      }
    }

    document.addEventListener('contextmenu', onContextMenu)
    document.addEventListener('dragstart', onDragStart)
    document.addEventListener('selectstart', onSelectStart)
    document.addEventListener('keydown', onKeyDown)

    return () => {
      document.removeEventListener('contextmenu', onContextMenu)
      document.removeEventListener('dragstart', onDragStart)
      document.removeEventListener('selectstart', onSelectStart)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [])
}
